/**
 * COMPONENT: BedTimeline
 * Frise de progression du BED (cahier L125-127).
 *
 * - un état par étape : complète ✓ / en cours / manquant ⚠ ;
 * - CLIQUABLE : on saute directement à une étape déjà visitée sans repasser
 *   par les autres ;
 * - mobile-first : la frise défile horizontalement sur petit écran, sans
 *   jamais faire déborder la page.
 */

import { AlertTriangle, Check } from 'lucide-react';
import { BED_STEPS, type BedStepId, type BedStepState } from '../../lib/bed/wizard-model';

interface BedTimelineProps {
  current: BedStepId;
  states: Record<BedStepId, BedStepState>;
  /** Étapes déjà ouvertes au moins une fois — seules elles sont cliquables. */
  visited: Set<BedStepId>;
  onNavigate: (step: BedStepId) => void;
}

const STATE_LABEL: Record<BedStepState, string> = {
  complete: 'complète',
  warning: 'informations à fournir plus tard',
  partial: 'en cours',
  todo: 'à remplir',
};

function bulletClasses(state: BedStepState, isCurrent: boolean): string {
  if (isCurrent) return 'bg-zinc-900 text-white ring-4 ring-zinc-900/10';
  switch (state) {
    case 'complete':
      return 'bg-emerald-600 text-white';
    case 'warning':
      return 'bg-amber-500 text-white';
    case 'partial':
      return 'bg-white text-zinc-900 border-2 border-zinc-900';
    default:
      return 'bg-white text-zinc-400 border border-zinc-300';
  }
}

export function BedTimeline({ current, states, visited, onNavigate }: BedTimelineProps) {
  return (
    <nav aria-label="Progression du bulletin" className="-mx-1 overflow-x-auto pb-1">
      <ol className="flex min-w-max items-start gap-1 px-1">
        {BED_STEPS.map((step, position) => {
          const state = states[step.id];
          const isCurrent = step.id === current;
          const isReachable = visited.has(step.id) || isCurrent;

          return (
            <li key={step.id} className="flex items-start">
              {position > 0 && (
                <span
                  aria-hidden="true"
                  className={`mt-4 h-px w-4 shrink-0 sm:w-8 ${
                    states[BED_STEPS[position - 1].id] === 'complete'
                      ? 'bg-emerald-500'
                      : 'bg-zinc-200'
                  }`}
                />
              )}

              <button
                type="button"
                onClick={() => isReachable && onNavigate(step.id)}
                disabled={!isReachable}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={`Étape ${step.index} — ${step.title} : ${STATE_LABEL[state]}`}
                className={`flex min-w-[4.75rem] shrink-0 flex-col items-center gap-1 rounded-md px-2 py-1 ${
                  isReachable ? 'cursor-pointer hover:bg-zinc-50' : 'cursor-not-allowed opacity-60'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors ${bulletClasses(
                    state,
                    isCurrent
                  )}`}
                >
                  {state === 'complete' && !isCurrent ? (
                    <Check className="h-4 w-4" />
                  ) : state === 'warning' && !isCurrent ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    step.index
                  )}
                </span>
                {/* Libellé sur une seule ligne : la frise défile
                    horizontalement plutôt que de couper « Récapitulatif » en
                    morceaux — et un libellé ne recouvre jamais son voisin. */}
                <span
                  className={`whitespace-nowrap text-center text-[11px] leading-tight sm:text-xs ${
                    isCurrent ? 'font-semibold text-zinc-900' : 'text-zinc-600'
                  }`}
                >
                  {step.title}
                </span>
                {state === 'warning' && (
                  <span className="whitespace-nowrap text-center text-[10px] font-medium leading-tight text-amber-600">
                    à compléter
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
